import { createFileRoute } from "@tanstack/react-router";
import { BrainingLogo } from "@/braining/components/braining-logo";

/**
 * Публичная страница о данных.
 *
 * Заведена не ради буквы закона, а ради конкретного требования Google: чтобы
 * перевести приложение OAuth в статус «В продакшене», консоль требует адрес
 * сайта и ссылку на политику конфиденциальности. Без публикации Google
 * отзывает доступ через семь дней, и выгрузка в Google-таблицы отваливается.
 *
 * Отсюда и требования к странице: открыта без входа (см. PUBLIC_PATHS в
 * `__root.tsx`), лежит на том же домене, что и приложение, и описывает ровно
 * то, что система делает на самом деле. Правку текста согласовывает владелец;
 * при передаче агентству меняются адрес почты и название оператора.
 */
export const Route = createFileRoute("/privacy")({
  head: () => ({ meta: [{ title: "Данные и доступы — Braining" }] }),
  component: PrivacyPage,
});

const UPDATED = "21 октября 2026";

function PrivacyPage() {
  return (
    <div className="mx-auto max-w-2xl px-5 py-12">
      <div className="mb-8">
        <BrainingLogo className="h-10 w-auto text-foreground" />
      </div>

      <h1 className="text-2xl font-semibold tracking-tight">Данные и доступы</h1>
      <p className="mt-1 text-sm text-muted-foreground">Обновлено {UPDATED}</p>

      <div className="mt-8 space-y-6 text-sm leading-relaxed">
        <section className="space-y-2">
          <h2 className="text-base font-semibold">Что это за система</h2>
          <p>
            Калькулятор смет рекламного агентства Braining Group. Внутренний инструмент: доступ
            только у сотрудников агентства, по логину и паролю, которые заводит администратор.
            Публичной регистрации нет.
          </p>
          <p>
            С октября 2026 в системе есть кабинет партнёра — для подрядчиков агентства, на отдельном
            адресе partners.braining.group. Подрядчик попадает туда только по приглашению
            сотрудника, заводит свои реквизиты и видит договоры с агентством.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold">Какие данные обрабатываются</h2>
          <ul className="list-disc space-y-1.5 pl-5">
            <li>Учётные записи сотрудников: почта, роль, зашифрованный пароль.</li>
            <li>
              Материалы проектов, которые сотрудник загружает сам или открывает системе на Google
              Drive: брифы, переписка с клиентом, сметы.
            </li>
            <li>Сами калькуляторы: состав работ, часы, ставки, суммы.</li>
            <li>
              Данные подрядчиков: ФИО, паспортные данные, ИНН, адреса, банковские реквизиты,
              контакты; справки и карточки с реквизитами, которые подрядчик прикладывает сам;
              договоры с агентством.
            </li>
          </ul>
          <p>
            Данные подрядчиков обрабатываются с их согласия и только для заключения и исполнения
            договоров, выставления счетов и подготовки закрывающих документов. Оператор этих данных
            — агентство Braining. Скан паспорта система не запрашивает и не хранит. Профилей и
            рекламы нет.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold">Доступ к Google</h2>
          <p>Система пользуется двумя доступами, и оба узкие.</p>
          <ul className="list-disc space-y-1.5 pl-5">
            <li>
              <span className="font-medium">Чтение материалов.</span> Сервисный аккаунт читает
              только те папки Google Drive, которые сотрудник расшарил на его почту вручную. Сам он
              ничего не ищет и не открывает.
            </li>
            <li>
              <span className="font-medium">Выгрузка калькуляторов.</span> Область доступа{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">drive.file</code> — приложение
              видит только те файлы, которые создало само, и кладёт их в отдельную папку в Диске
              подключившего. Остальные файлы аккаунта ему недоступны.
            </li>
          </ul>
          <p>
            Подключение аккаунта отзывается в любой момент: кнопкой «Отключить» на экране Google
            Drive внутри системы или в настройках Google-аккаунта, раздел «Сторонние приложения».
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold">Кому данные передаются</h2>
          <p>
            Текст материалов уходит языковой модели, чтобы разобрать бриф на состав работ. Модель
            вызывается через провайдера OpenRouter.
          </p>
          <p>Для подрядчиков — ещё три сервиса:</p>
          <ul className="list-disc space-y-1.5 pl-5">
            <li>
              документы, которые подрядчик прикладывает, читает та же языковая модель, в том числе
              за пределами РФ, — чтобы переписать реквизиты в анкету;
            </li>
            <li>DaData — сверка ИНН с ЕГРЮЛ и ЕГРИП;</li>
            <li>ФНС — проверка статуса самозанятого по ИНН.</li>
          </ul>
          <p>
            Больше данные никуда не передаются: рекламы, аналитики и продажи данных в системе нет.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold">Где данные хранятся</h2>
          <p>
            На собственном сервере системы: база данных и файловое хранилище. Доступ к серверу — у
            администратора. Резервные копии уходят в хранилище агентства только зашифрованными.
            Данные хранятся, пока агентство пользуется системой; по запросу удаляются.
          </p>
          <p>
            Данные подрядчика видят только сотрудники, которые ведут документы с подрядчиками.
            Каждое открытие карточки и скачивание документов записывается в журнал: кто и когда.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold">Подрядчику</h2>
          <p>
            Свои реквизиты можно посмотреть и поправить в кабинете в любой момент. Узнать, кто из
            сотрудников открывал ваши данные, удалить их или отозвать согласие — письмом тому, кто
            вас приглашал, или по почте ниже.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold">Связаться</h2>
          <p>
            По вопросам о данных и доступах:{" "}
            <a href="mailto:kenest@gmail.com" className="text-primary hover:underline">
              kenest@gmail.com
            </a>
          </p>
        </section>
      </div>
    </div>
  );
}
